import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Spinner,
  Card,
  Badge,
} from "react-bootstrap";
import {
  BarChart3,
  RefreshCcw,
  MessageSquare,
  Zap,
  CalendarDays,
  User,
  Trophy,
} from "lucide-react";
import axios from "axios";

// Layout Components
import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
import { useAuth } from "./Home1";

const API_URL = "https://beaconsms.com/oldsite/api/message_stats_api.php";

const MessageStats = () => {
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.id === "71";

  // Default date range: Today
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      // API call with startDate and endDate parameters
      const res = await axios.get(
        `${API_URL}?adminId=71&startDate=${startDate}&endDate=${endDate}`,
      );
      setStats(res.data);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, startDate, endDate]);

  useEffect(() => {
    document.title = "Message Statistics - beaconSMS";
    fetchStats();
  }, [fetchStats]);

  // UI Helper: Sets range to last 7 days
  const setLast7Days = () => {
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    setStartDate(lastWeek.toISOString().split("T")[0]);
    setEndDate(today);
  };

  if (authLoading)
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
      </div>
    );

  if (!isAdmin) {
    return (
      <Container className='mt-5 text-center p-5 bg-white border rounded shadow-sm'>
        <h2 className='fw-bold text-danger'>Access Denied</h2>
        <p className='text-muted'>
          Administrative privileges are required to view these statistics.
        </p>
      </Container>
    );
  }

  return (
    <Container>
      <Header user={user} />
      <Row className='mt-3 px-md-3'>
        {/* Sidebar */}
        <Col md='3' className='mb-3 mb-md-0'>
          <Logout Menu='Login Menu' user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
        </Col>

        {/* Main Dashboard */}
        <Col md='9'>
          <div className='bg-white p-4 border rounded shadow-sm min-vh-100'>
            {/* Header & Date Controls */}
            <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 border-bottom pb-3'>
              <div className='d-flex align-items-center text-primary mb-3 mb-md-0'>
                <BarChart3 className='me-2' size={24} />
                <h4 className='mb-0 fw-bold'>Message Statistics</h4>
              </div>

              <div className='d-flex flex-wrap align-items-center gap-2'>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  onClick={setLast7Days}
                >
                  Last 7 Days
                </Button>
                <Form.Control
                  type='date'
                  size='sm'
                  style={{ width: "auto" }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span className='small text-muted'>to</span>
                <Form.Control
                  type='date'
                  size='sm'
                  style={{ width: "auto" }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <Button variant='primary' size='sm' onClick={fetchStats}>
                  <RefreshCcw size={14} className={loading ? "fa-spin" : ""} />
                </Button>
              </div>
            </div>

            {/* Summary KPI Cards */}
            <Row className='mb-4'>
              <Col md={6} className='mb-3 mb-md-0'>
                <Card className='bg-primary text-white border-0 shadow-sm overflow-hidden'>
                  <Card.Body
                    className='d-flex align-items-center position-relative'
                    style={{ zIndex: 1 }}
                  >
                    <div className='rounded-circle bg-white bg-opacity-25 p-3 me-3'>
                      <MessageSquare size={28} />
                    </div>
                    <div>
                      <div className='small opacity-75 fw-medium text-uppercase'>
                        Range Total Messages
                      </div>
                      <h2 className='mb-0 fw-bold'>
                        {stats?.summary?.grand_total_messages || 0}
                      </h2>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className='bg-success text-white border-0 shadow-sm overflow-hidden'>
                  <Card.Body
                    className='d-flex align-items-center position-relative'
                    style={{ zIndex: 1 }}
                  >
                    <div className='rounded-circle bg-white bg-opacity-25 p-3 me-3'>
                      <Zap size={28} />
                    </div>
                    <div>
                      <div className='small opacity-75 fw-medium text-uppercase'>
                        Range Total Units
                      </div>
                      <h2 className='mb-0 fw-bold'>
                        {Number(stats?.summary?.grand_total_units || 0).toFixed(
                          2,
                        )}
                      </h2>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Stats Table */}
            <div className='table-responsive'>
              <Table hover className='align-middle border rounded shadow-xs'>
                <thead
                  className='bg-light text-uppercase'
                  style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                >
                  <tr>
                    <th>User / Account</th>
                    <th className='text-center'>Total Messages</th>
                    <th className='text-center'>Total Units</th>
                    <th className='text-end'>Rank</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: "14px" }}>
                  {loading ? (
                    <tr>
                      <td colSpan='4' className='text-center py-5'>
                        <Spinner animation='border' variant='primary' />
                      </td>
                    </tr>
                  ) : stats?.user_stats?.length > 0 ? (
                    stats.user_stats.map((s, i) => (
                      <tr key={i}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='bg-light rounded p-2 me-2'>
                              <User size={16} className='text-muted' />
                            </div>
                            <div>
                              <div className='fw-bold text-primary'>
                                @{s.username}
                              </div>
                              <div
                                className='small text-muted'
                                style={{ fontSize: "11px" }}
                              >
                                {s.name || "Unnamed User"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className='text-center fw-bold'>
                          {s.total_messages.toLocaleString()}
                        </td>
                        <td className='text-center text-success fw-medium'>
                          {Number(s.total_units).toFixed(2)}{" "}
                          <small className='text-muted'>units</small>
                        </td>
                        <td className='text-end'>
                          <Badge
                            bg={i === 0 ? "warning" : "light"}
                            className={
                              i === 0 ? "text-dark" : "text-muted border"
                            }
                          >
                            {i === 0 && <Trophy size={10} className='me-1' />}#
                            {i + 1}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan='4' className='text-center py-5 text-muted'>
                        No activity found for the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default MessageStats;
